// How the Indicators dialog orders what matches the search, as TradingView does:
// the indicator the text names first ("EMA" → Exponential Moving Average), then
// names starting with it, then names containing it, then description matches.

export interface Searchable {
  readonly name: string
  readonly shortName: string
  readonly description?: string | undefined
}

/** Lower is better; null when it doesn't match. `query` is trimmed and lower case. */
export const searchRank = (query: string, entry: Searchable): number | null => {
  if (query === "") return 0
  const name = entry.name.toLowerCase()
  const short = entry.shortName.toLowerCase()
  if (short === query || name === query) return 0
  if (short.startsWith(query)) return 1
  if (name.startsWith(query)) return 2
  // A word of the name starts with it ("moving" → Exponential Moving Average).
  if (name.split(/[\s()/,-]+/).some((word) => word.startsWith(query))) return 3
  if (name.includes(query)) return 4
  if (short.includes(query)) return 5
  if ((entry.description ?? "").toLowerCase().includes(query)) return 6
  return null
}

/** The matches, best first, alphabetical within a rank. */
export const searchIndicators = <E extends Searchable>(entries: ReadonlyArray<E>, query: string): Array<E> =>
  entries
    .flatMap((entry) => {
      const rank = searchRank(query, entry)
      return rank === null ? [] : [{ entry, rank }]
    })
    .sort((a, b) => a.rank - b.rank || a.entry.name.localeCompare(b.entry.name))
    .map(({ entry }) => entry)
